<script setup>
    import { thumbailForSpeaker } from '@/composables/helpers/speakers';

    const defaultSpeakerImg = '/assets/img/placeholder-speaker.jpg';

    const props = defineProps({
        speaker: {
            type: Object,
            required: true,
        },
    });

    const image = computed(() => thumbailForSpeaker(props.speaker));
</script>

<template>
    <div class="speaker-hero flex flex-col gap-[28px] lg:flex-row lg:items-start lg:gap-[80px]">
        <div class="speaker-hex-portrait relative w-[260px] h-[260px] md:w-[440px] md:h-[440px] shrink-0 overflow-hidden">
            <img :src="image?.url || defaultSpeakerImg"
                :alt="`${speaker.name} profile photo`"
                loading="lazy"
                class="w-full h-full object-cover" />
        </div>

        <div class="flex flex-col gap-[32px] text-[var(--magenta)] min-w-0">
            <div class="flex flex-col gap-[19px]">
                <h2 class="font-hubspot-serif font-normal m-0 text-[40px] md:text-[60px] leading-[1.1]"
                    v-html="speaker.name" />
                <p v-if="speaker.job_title || speaker.company"
                    class="font-hubspot-sans font-medium text-[12px] leading-[1.7] tracking-[0.84px] uppercase m-0">
                    <span v-if="speaker.job_title" v-html="speaker.job_title" />
                    <span v-if="speaker.company">&nbsp;@ {{ speaker.company }}</span>
                </p>
            </div>

            <FragmentsSocial class="speaker-hero-socials"
                :linkedin="speaker.linkedin_page"
                :instagram="speaker.instagram_page"
                :tiktok="speaker.tiktok_channel"
                :facebook="speaker.facebook_page"
                :youtube="speaker.youtube_channel"
                :bluesky="speaker.bluesky_page"
                :threads="speaker.threads_page"
                :twitter="speaker.twitter_handle ? `https://x.com/${speaker.twitter_handle}` : false"
                :website="speaker.speaker_website" />

            <div v-if="speaker.bio"
                class="speaker-bio font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.32px]"
                v-html="speaker.bio" />
        </div>
    </div>
</template>

<style>
.speaker-hero .speaker-hex-portrait {
    mask-image: url('/img/2026/speakers/speaker-hex.svg');
    -webkit-mask-image: url('/img/2026/speakers/speaker-hex.svg');
    mask-size: 100% 100%;
    -webkit-mask-size: 100% 100%;
    mask-repeat: no-repeat;
    -webkit-mask-repeat: no-repeat;
}

/* Figma uses 42px social icons, FragmentsSocial defaults to 36px — override */
.speaker-hero-socials .links {
    gap: 15px;
}

.speaker-hero-socials .links li {
    margin-bottom: 0;
}

.speaker-hero-socials .links li svg {
    width: 42px;
    height: 42px;
    display: block;
}

.speaker-hero .speaker-bio :is(p, ul, ol):not(:last-child) {
    margin-bottom: 1rem;
}
</style>
